/**
 * In-app trial banner. Two states driven by `/billing/status`:
 *
 *   1. Active trial, days_left > 0 → "X days left in your free trial" + Upgrade CTA
 *   2. Expired, is_read_only === true → full-width red banner "Your trial has
 *      ended. Choose a plan to keep going" + Upgrade CTA
 *
 * Rendered inside `Layout.jsx` just below the top bar so it's visible on
 * every signed-in admin screen. Techs don't see it.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";

export default function TrialBanner() {
  const location = useLocation();
  const [status, setStatus] = useState(null);

  // Re-fetch on route change so an upgrade in /admin/billing instantly
  // clears the banner on the next page the admin lands on.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/status");
        if (!cancelled) setStatus(data);
      } catch {
        // Non-fatal — hide the banner if we can't read status.
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (!status) return null;
  const trial = status.trial || {};
  const onBillingPage = location.pathname.startsWith("/admin/billing");

  // State 2 — read-only (trial elapsed, no paid plan).
  if (trial.is_read_only) {
    if (onBillingPage) return null; // the billing page itself is the fix
    return (
      <div
        className="w-full bg-red-600 text-white px-4 py-3 flex items-center justify-between gap-4 shadow-md"
        data-testid="trial-ended-banner"
      >
        <div className="flex items-center gap-3 min-w-0">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-bold text-sm md:text-base">Your trial has ended.</p>
            <p className="text-xs md:text-sm opacity-90 truncate">
              Your account is read-only. Choose a plan to keep going — your data is safe.
            </p>
          </div>
        </div>
        <Link
          to="/admin/billing"
          data-testid="trial-ended-upgrade-btn"
          className="shrink-0 bg-white text-red-700 font-bold px-4 py-2 rounded-full text-sm hover:bg-slate-100"
        >
          Choose a plan
        </Link>
      </div>
    );
  }

  // State 1 — active trial, show countdown. Hide once a paid plan is active.
  if (!trial.is_trial || trial.days_left == null) return null;
  if (onBillingPage) return null;
  const days = trial.days_left;
  const urgency = days <= 3;
  const bg = urgency ? "bg-amber-500" : "bg-sky-600";
  const label =
    days <= 0
      ? "Last day of your free trial"
      : days === 1
      ? "1 day left in your free trial"
      : `${days} days left in your free trial`;
  return (
    <div
      className={`w-full ${bg} text-white px-4 py-2 flex items-center justify-between gap-4`}
      data-testid="trial-countdown-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Clock className="h-4 w-4 flex-shrink-0" />
        <p className="text-sm font-semibold truncate">
          {label} · no card on file
        </p>
      </div>
      <Link
        to="/admin/billing"
        data-testid="trial-upgrade-btn"
        className="shrink-0 bg-white text-slate-900 font-bold px-3 py-1 rounded-full text-xs hover:bg-slate-100"
      >
        Upgrade
      </Link>
    </div>
  );
}
